import React from 'react';
import { Field, FieldArray } from 'redux-form';
// import { renderDollar, renderCurrency } from '../Utils/RenderField';
import NumberFormat from 'react-number-format';
import { months } from '../../../../Utils/months';
import { 
    useComparativoTotalsLocal, 
    LocalComparativoCurrencyDisplay,
    formatComparativoCurrency 
} from '../Utils/ComparativoFlujoCajaComponents';
import { CustomCurrencyDisabled, CustomDollarDisabled, renderCurrency, renderDollar, renderFieldDisabled } from '../../../../Utils/renderField/renderField';


const TableRow = ({ system, index, fields, change, currency, localTotals }) => {
    const _months = months;
    const is_manual = fields.get(index).is_manual;
    const isTitulo = fields.get(index).isTitulo;
    const currentRow = fields.get(index);
    const isSalida = currentRow.type_of_flow === "salidas" || 
                    (currentRow.name && currentRow.name.includes("PAGOS")) || 
                    (currentRow.name && currentRow.name.includes("SALIDAS"));
    const isFinal = fields.get(index).isFinal;
    const isOpeningBalance = fields.get(index).isOpeningBalance;

    // Obtener valores calculados localmente para las filas del resumen
    const getLocalValue = (month) => {
        if (!localTotals) return 0;
        
        // Mapear indices del resumen con los totales locales
        switch (index) {
            case 1: // ENTRADAS
                return localTotals.entradas[month] || 0;
            case 2: // SALIDAS  
                return -(localTotals.salidas[month] || 0); // Negativo para mostrar como salida
            case 3: // FLUJO NETO
                return localTotals.flujoNeto[month] || 0;
            case 4: // SALDO FINAL
                return localTotals.saldoFinal[month] || 0;
            default:
                return parseFloat(currentRow[month]) || 0;
        }
    };

    React.useEffect(() => {
        change(`${system}.is_dollar`, currency === 'USD');
    }, [currency, system, change]);

    const calculateTotalForRow = () => {
        // Para la fila del saldo inicial (índice 0), solo usar enero
        if (index === 0) {
            const systemData = fields.get(index);
            return parseFloat(systemData && systemData.enero) || 0;
        }
        
        // Para filas del resumen (índices 1-4), usar totales locales
        if (localTotals && index >= 1 && index <= 4) {
            switch (index) {
                case 1: return localTotals.entradas.total || 0;
                case 2: return -(localTotals.salidas.total || 0); // Negativo para salidas
                case 3: return localTotals.flujoNeto.total || 0;
                case 4: return localTotals.saldoFinal.total || 0;
                default: break;
            }
        }

        // Para otras filas, calcular normalmente
        const systemData = fields.get(index);
        if (!systemData) return 0;

        let totalForRow = _months.reduce((accumulatedTotal, month) => {
            const value = parseFloat(systemData[month]) || 0;
            accumulatedTotal += value;
            return accumulatedTotal;
        }, 0);
        return totalForRow;
    };
    
    const textStyle = isSalida ? { color: '#dc3545', fontWeight: '500' } : {};
    const cellStyle = { ...textStyle, width: "8%" };
    const finalStyle = isFinal ? { backgroundColor: "#F0FFC7", ...cellStyle } : cellStyle;
    
    return (
        <React.Fragment key={index}>
            <tr key={`system-${index}`} className={fields.get(index).total < 0 && 'red-row'} 
                style={isFinal ? { backgroundColor: "#F0FFC7" } : {}}>
                <td className='first-column table-form-fixed-cell-left-0' style={!isFinal ? {} : { backgroundColor: "#F0FFC7", minWidth: "200px" }} >
                    <Field
                        name={`${system}.name`}
                        type="text"
                        isTextarea={!isTitulo}
                        applyStyles={!isTitulo}
                        style={textStyle}
                        component={renderFieldDisabled}
                        className="form-control"
                        placeholder='Resumen'
                    />
                </td>
                <td className='table-form-fixed-cell-left-1' style={finalStyle}>
                    <LocalComparativoCurrencyDisplay 
                        value={calculateTotalForRow()} 
                        currency={currency} 
                        showNegativeInParentheses={isSalida || calculateTotalForRow() < 0} 
                    />
                </td>
                {!isOpeningBalance ?
                    months.map((month) => {
                        // Para filas calculadas (resumen), mostrar valores locales
                        const shouldShowCalculatedValue = localTotals && index >= 1 && index <= 4;
                        
                        if (shouldShowCalculatedValue) {
                            const localValue = getLocalValue(month);
                            return (
                                <td key={month} style={!isFinal ? { width: "6.9%" } : { width: "6.9%", backgroundColor: "#F0FFC7" }}>
                                    <div style={{ 
                                        padding: '0.375rem 0.75rem', 
                                        textAlign: 'right',
                                        color: (isSalida || localValue < 0) ? '#dc3545' : 'inherit',
                                        fontWeight: (isSalida || localValue < 0) ? '500' : 'normal'
                                    }}>
                                        <LocalComparativoCurrencyDisplay 
                                            value={localValue} 
                                            currency={currency} 
                                            showNegativeInParentheses={isSalida || localValue < 0}
                                        />
                                    </div>
                                </td>
                            );
                        }
                        
                        // Para campos editables (como saldo inicial), mantener el Field normal
                        return (
                            <td key={month} style={!isFinal ? { width: "6.9%" } : { width: "6.9%", backgroundColor: "#F0FFC7" }}>
                               <Field
                                    name={`${system}.${month}`}
                                    component={is_manual ? (currency === 'USD' ? renderDollar : renderCurrency) : (currency === 'USD' ? CustomDollarDisabled : CustomCurrencyDisabled)}
                                    placeholder={fields.get(index).is_dollar ? '$ 0.00' : 'Q 0.00'}
                                    disabled={!is_manual}
                                    style={isSalida ? {
                                        color: '#dc3545',
                                        fontWeight: '500',
                                        borderColor: '#dc3545',
                                        backgroundColor: '#fff5f5'
                                    } : {}}
                                    isSalida={isSalida}
                                />
                            </td>
                        );
                    })
                    :
                    <React.Fragment>
                        <td style={!isFinal ? { width: "6.9%" } : { width: "6.9%", backgroundColor: "#F0FFC7" }}>
                            <Field
                                name={`${system}.enero`}
                                component={currency === 'USD' ? renderDollar : renderCurrency}
                                placeholder={fields.get(index).is_dollar ? '$ 0.00' : 'Q 0.00'}
                                disabled={false}
                                style={isSalida ? {
                                    color: '#dc3545',
                                    fontWeight: '500',
                                    borderColor: '#dc3545',
                                    backgroundColor: '#fff5f5'
                                } : {}}
                            />
                        </td>
                        {/* Celdas vacías para el resto de los meses */}
                        {months.slice(1).map((month) => (
                            <td key={month} style={!isFinal ? { width: "6.9%" } : { width: "6.9%", backgroundColor: "#F0FFC7" }}>
                                &nbsp;
                            </td>
                        ))}
                    </React.Fragment>
                }
            </tr>
        </React.Fragment>
    );
};

const GenericTable = ({ fields, allValues, change, currency }) => {
    // Calcular totales localmente (similar a ResultadosTable)
    const localTotals = useComparativoTotalsLocal(allValues);

    const columnas = Array.from({ length: 12 }, (_, index) => (
        <th key={index} style={{ width: "6.9%" }}>{index + 1}</th>
    ));

    return (
        <div className=" col-sm-12 form-group np-r p-0">
            <div className='table-responsive'>
                <table className="table table-striped mb-0">
                    <thead className="thead-not-cursor">
                        <tr>
                            <th className='first-column table-form-fixed-cell-left-0 titulo-left-padded'>MES</th>
                            <th className='table-form-fixed-cell-left-1 text-right-padded-sm'>TOTAL</th>
                            {columnas}
                        </tr>
                    </thead>
                    <tbody style={{ backgroundColor: "#F0FFC7" }}>
                        {fields.map((system, index) => (
                            <TableRow 
                                key={index} 
                                system={system} 
                                index={index} 
                                fields={fields} 
                                change={change} 
                                currency={currency} 
                                localTotals={localTotals}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ResumenTable = ({ allValues, change, currency }) => {
    return (
        <div>
            <FieldArray
                name={`summary`}
                component={GenericTable}
                allValues={allValues}
                change={change}
                currency={currency}
            />
        </div>
    );
};

export default ResumenTable;
